import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import makeStyles from '@material-ui/core/styles/makeStyles';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { color } from '../../../app/theme';
import { saveScheduledUpdatesSetting } from '../../../features/aiBotSlice';
import Button from '../../shared/Button';
import Dropdown from '../../shared/Dropdown';
import Switch from '../../shared/Switch';

const useStyles = makeStyles(
  () => ({
    root: { zIndex: '9999 !important' },
    paper: {
      width: 960,
      maxWidth: 'unset',
      borderRadius: 12,
      boxShadow:
        '0 30px 66px 0 rgba(0, 0, 0, 0.09), 0 8px 58px 0 rgba(0, 0, 0, 0.05)',
    },
    title: {
      fontSize: 20,
      lineHeight: 1,
      color: color.$3,
      textAlign: 'center',
      padding: 24,
    },
    contentRoot: {
      backgroundColor: color.$19,
      padding: 24,
    },
    actionRoot: {
      backgroundColor: color.$19,
      padding: '0 24px 24px',
    },
    enabled: {
      fontSize: '15px',
      fontWeight: '500',
      lineHeight: '15px',
      color: color.$3,
    },
    nextExeTime: {
      fontSize: '15px',
      fontWeight: '400',
      lineHeight: '21px',
      color: color.$10,
      marginTop: 12,
    },
    time: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      lineHeight: 1,
      color: color.$13,
      '& > span': {
        margin: '0 8px',
      },
    },
    dropdown: {
      width: 80,
    },
    tip: {
      fontSize: '15px',
      lineHeight: '21px',
      color: color.$13,
      marginBottom: 6,
    },
  }),
  { name: 'ScheduledUpdateModal' },
);

function convertSecondsToMonthsDays(seconds) {
  const secondsPerDay = 86400;
  const approximateDaysPerMonth = 30;

  const totalDays = Math.floor(seconds / secondsPerDay);
  const months = Math.floor(totalDays / approximateDaysPerMonth);
  const days = totalDays % approximateDaysPerMonth;

  return { months, days };
}

function generateArray(start, interval, count) {
  const array = [];
  for (let i = 0; i < count; i += 1) {
    const value = start + i * interval;
    array.push({ text: String(value), value });
  }
  return array;
}
const monthItem = generateArray(0, 1, 31);

const dayItem = generateArray(0, 15, 2);

function ScheduledUpdateModal({ isOpen, onClose }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId, id } = useParams();
  const [enabled, setEnabled] = useState();
  const [month, setMonth] = useState(0);
  const [day, setDay] = useState(0);
  const [monthStyle, setMonthStyle] = useState({});
  const [dayStyle, setDayStyle] = useState({});
  const { data, pathKey } = useSelector(store => store.aiBot);
  const { scheduledUpdatesSetting } = data || {};
  const { nextExecuteTime } = scheduledUpdatesSetting || {};

  const monthRef = useRef(null);
  const dayRef = useRef(null);

  async function handleClickDialog() {
    await dispatch(
      saveScheduledUpdatesSetting(orgId, id, {
        scheduledUpdatesSetting: {
          ...scheduledUpdatesSetting,
          enabled,
          time: month * 30 * 86400 + day * 86400,
        },
      }),
    );
    onClose();
  }

  function handleCloseDialog() {
    onClose();
  }

  useEffect(() => {
    const { time = 0 } = scheduledUpdatesSetting || {};
    const convertResult = convertSecondsToMonthsDays(time);

    setEnabled(scheduledUpdatesSetting?.enabled);
    setMonth(convertResult?.months);
    setDay(convertResult?.days);
  }, [scheduledUpdatesSetting]);

  useEffect(() => {
    const getStyle = ref => {
      const rect = ref?.current?.getBoundingClientRect();
      if (rect) {
        return {
          position: 'fixed',
          width: 210,
          maxHeight: 150,
          top: `${rect.top + 44}px`,
          left: `${rect.right - 80}px`,
        };
      }
      return {};
    };

    setTimeout(() => {
      setMonthStyle(getStyle(monthRef));
      setDayStyle(getStyle(dayRef));
    }, 500);
  }, [monthRef, dayRef]);

  return (
    <>
      <Dialog
        open={isOpen}
        className={classes.root}
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle className={classes.title} disableTypography>
          {t(`${pathKey}:scheduledUpdate`)}
        </DialogTitle>
        <DialogContent className={classes.contentRoot}>
          <div className={classes.enabled}>
            <span style={{ marginRight: 12 }}>
              {t(`${pathKey}:scheduledUpdate`)}
            </span>
            <Switch onChange={() => setEnabled(!enabled)} checked={!!enabled} />
          </div>
          <div className={classes.tip}>
            {t(`${pathKey}:scheduledUpdateTip`)}
          </div>
          <div className={classes.time}>
            <div ref={monthRef}>
              <Dropdown
                className={classes.dropdown}
                menuStyle={monthStyle}
                menuItems={monthItem}
                value={month}
                onChange={v => setMonth(v)}
              />
            </div>
            <span>月</span>
            <div ref={dayRef}>
              <Dropdown
                className={classes.dropdown}
                menuStyle={dayStyle}
                menuItems={dayItem}
                value={day}
                onChange={v => setDay(v)}
              />
            </div>
            <span>天</span>
          </div>
          {nextExecuteTime && (
            <div className={classes.nextExeTime}>
              {t(`${pathKey}:scheduledUpdateNextExeTime`, {
                date: moment(nextExecuteTime).format('YYYY-MM-DD'),
              })}
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.actionRoot}>
          <Button topic="primaryBlue" onClick={handleCloseDialog}>
            {t('modal.cancel')}
          </Button>
          <Button
            topic="primaryBlueHighLight"
            onClick={handleClickDialog}
            isDisable={day === 0 && month === 0}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ScheduledUpdateModal;
